import {RequestManager} from "../../../../stores/managers/RequestManager";
import type {AxiosResponse} from "axios";
import {from} from "rxjs";
import type {MithraMaterializedApi} from "../../../../services/MithraMaterializedApi";
import type {BagStore} from "../../../../stores/BagStore";
import type ProfileStore from "../../../../stores/ProfileStore";
import {makeAutoObservable, reaction} from "mobx";
import type {MatCommonKpiSerializer} from "../../../../services/ApiTypes";
import {PpvPageController} from "./PpvPageController";
import type {PpvGroupRowState} from "../classes/PpvGroupRowState";
import {TaxonomyCategoryProvider} from "../../../../stores/TaxonomyCategoryProvider";
import type {PpvGroupFilter} from "../classes/PpvApiClasses";
import {OpportunityStatusEnum} from "../../../../services/classes/OpportunityClasses";
import {generatePath} from "react-router";
import {routes} from "../../../../routing/routes";
import type {PpvPartRowState} from "../classes/PpvPartRowState";
import {analyticsOpportunityEvent} from "../../../../services/analytics/AnalyticEvents";
import {getOpportunityStatusName} from "../../../../pages/opportunity/utils/OpportunityStatusVisual";
import type AuthStore from "../../../../authentication/AuthStore";
import type {NavigateFunction} from "react-router-dom";

type PpvOpportunityTotalSavingsFilter = {
    databag: number
}

type PpvOpportunityTotalSavings = {
    total_expected_savings: number | null
}

export class PpvControllerStore {
    databag_id: number | undefined;
    maxTaxonomySize = 2; // Do not allow to go deeper than L2 initially
    readonly categories: TaxonomyCategoryProvider;
    readonly ppvPageController: PpvPageController;

    isEditNoteDialogOpen: boolean = false
    isDeleteGroupModalOpen: boolean = false
    ppvRowToEdit: PpvGroupRowState | undefined = undefined
    ppvGroupToDelete: PpvGroupRowState | undefined = undefined
    ppvItemToDelete: PpvPartRowState | undefined = undefined

    /**
     * Special request manager to manage the top root result. To show the total
     */
    readonly _rootStatRequestManager = new RequestManager<{databag: number}, AxiosResponse<MatCommonKpiSerializer>>(
        ({databag}) => from(this.api.getCommonKpi(databag))
    );

    readonly _ppvTotalExpectedSavings = new RequestManager<PpvOpportunityTotalSavingsFilter, AxiosResponse<PpvOpportunityTotalSavings>>(
        databag => from(this.api.getPpvTotalExpectedSavings(Number(databag)))
    );

    // noinspection JSUnusedLocalSymbols
    constructor(
        private api: MithraMaterializedApi,
        private bagStore: BagStore,
        private profile: ProfileStore,
        private authStore: AuthStore,
    ) {
        makeAutoObservable(this)
        this.categories = new TaxonomyCategoryProvider(this.profile, this.api)
        this.ppvPageController = new PpvPageController(this, this.api, this.profile)


        reaction(() => [
            this.bagId,
        ] as const, () => {
            this.ppvPageController.loadData()
        })

    }

    init(databagId: number, taxonomySize: number) {
        this.databag_id = databagId;
        this.maxTaxonomySize = taxonomySize;
        if (databagId > 0) {
            this._rootStatRequestManager.request({databag: databagId})
            this._ppvTotalExpectedSavings.request({databag: databagId})
        } else {
            this._rootStatRequestManager.cleanup()
            this._ppvTotalExpectedSavings.cleanup()
        }
    }

    get totalExpectedSavings(): number | null {
        return this._ppvTotalExpectedSavings.result
            ? (this._ppvTotalExpectedSavings.result.data.total_expected_savings ?? 0)
            : null;
    }

    get bagId(): number {
        return this.bagStore.bagId;
    }

    setEditNoteDialogOpen(open: boolean) {
        this.isEditNoteDialogOpen = open;
    }

    setDeleteGroupModalOpen(open: boolean) {
        this.isDeleteGroupModalOpen = open;
    }

    setPpvGroupToDelete(group: PpvGroupRowState, part: PpvPartRowState) {
        this.ppvGroupToDelete = group;
        this.ppvItemToDelete = part;
    }

    saveNote(note: string, onUpdateNote: (note: string) => void) {
        if (!this.ppvRowToEdit) return;

        const opportunityId = this.ppvRowToEdit.data.opportunity?.id;
        const updateOrCreateOpportunity = opportunityId
            ? this.api.updatePpvOpportunity(opportunityId, {note}).then(() => onUpdateNote(note))
            : this.createNewOpportunity(this.ppvRowToEdit, this.ppvRowToEdit.data.opportunity?.status || OpportunityStatusEnum.PENDING_REVIEW).then(() => onUpdateNote(note));

        updateOrCreateOpportunity.finally(() => {
            this.ppvPageController.ppvPageStatisticsController.request(this.ppvPageController.ppvPageFilterController.statisticsFilter);
        });
    }

    changeStatus(groupedRow: PpvGroupRowState, enumValue: OpportunityStatusEnum) {
        analyticsOpportunityEvent({
            type: 'ppv',
            key: groupedRow.key,
            status: getOpportunityStatusName(enumValue),
            expected_savings: Number(groupedRow.data.ppv_opportunity),
            user: this.authStore.email,
        })
        this.setPpvRowToEdit(groupedRow);

        if (groupedRow?.data?.opportunity) {
            groupedRow.data.opportunity.status = enumValue;
        }

        if (!this.ppvRowToEdit) return;

        const opportunityId = this.ppvRowToEdit.data.opportunity?.id;
        const updateOrCreateOpportunity = opportunityId
            ? this.api.updatePpvOpportunity(opportunityId, {status: enumValue})
            : this.createNewOpportunity(groupedRow, enumValue);

        updateOrCreateOpportunity.finally(() => {
            this._ppvTotalExpectedSavings.request({databag: this.bagId});
            this.ppvPageController.ppvPageStatisticsController.request(this.ppvPageController.ppvPageFilterController.statisticsFilter);
            this.updateGroupedRowStatus(groupedRow);
        });
    }

    private createNewOpportunity(groupedRow: PpvGroupRowState, status: OpportunityStatusEnum) {
        if (!groupedRow.data.opportunity) {
            groupedRow.data.opportunity = {
                id: -1,
                ppv_group_key: groupedRow.key,
                databag: this.bagId,
                status: status,
                scope: this.ppvPageController.ppvPageFilterController.selectedFilter,
                note: '',
                initial_expected_savings: Number(groupedRow.data.ppv_opportunity),
                current_expected_savings: Number(groupedRow.data.ppv_opportunity),
                modified: new Date().toISOString()
            };
        }

        return this.api.createPpvOpportunity({
            ppv_group_key: groupedRow.key,
            databag: this.bagId,
            note: groupedRow.data.opportunity.note || '',
            status: status,
            scope: this.ppvPageController.ppvPageFilterController.selectedFilter,
            expected_savings: Number(groupedRow.data.ppv_opportunity)
        });
    }

    private updateGroupedRowStatus(groupedRow: PpvGroupRowState) {
        this.ppvPageController.ppvPageDataController.fetchGroups(this.ppvPageController.ppvPageFilterController.selectedFilter).then(supplierPages => {
            if (!supplierPages) return;

            const updatedGroup = supplierPages.find(response =>
                // ppv_key
                response.data.p_name === groupedRow.data.p_name
            );
            if (updatedGroup) {
                groupedRow.update(updatedGroup?.data);
            }
        });
    }

    setPpvRowToEdit(ppvRow: PpvGroupRowState) {
        this.ppvRowToEdit = ppvRow
    }

    goToPpvScope(navigate: NavigateFunction, filter: PpvGroupFilter) {
        this.ppvPageController.ppvPageFilterController.setActiveGroupFilter(filter)
        navigate(generatePath(routes.analytics_koi_ppv, {id: this.bagId}))
        this.ppvPageController.ppvPageFilterController.setActiveGroupFilter(filter)
    }

}
